import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Download, Eye, Loader2 } from 'lucide-react';

export default function BulkDownloadPanel({
  selectedCount,
  bulkLabel,
  disabled,
  disabledReason,
  running,
  progress,
  onConfirmBulk,
  isIdCard = false,
  canPreviewSheet = false,
  onPreviewSheet,
}) {
  const [open, setOpen] = useState(false);
  const [output, setOutput] = useState(isIdCard ? 'merged' : 'zip');

  useEffect(() => {
    if (isIdCard) setOutput('merged');
  }, [isIdCard]);

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  const handleConfirm = async () => {
    setOpen(false);
    await onConfirmBulk(output);
  };

  return (
    <div className="rounded-xl border border-border p-4 bg-card space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-medium">{bulkLabel}</p>
          <p className="text-sm text-muted-foreground">{selectedCount} student(s) selected</p>
          {isIdCard && (
            <p className="text-xs text-muted-foreground mt-1">
              Merged PDF uses your print sheet layout. ZIP gives one 2×3 PDF per student.
            </p>
          )}
          {disabledReason && <p className="text-sm text-amber-600 mt-1">{disabledReason}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {canPreviewSheet && onPreviewSheet && (
            <Button
              type="button"
              variant="outline"
              disabled={disabled || selectedCount === 0 || running}
              onClick={onPreviewSheet}
            >
              <Eye className="h-4 w-4 mr-2" />
              Bulk preview
            </Button>
          )}
          <Button
            type="button"
            disabled={disabled || selectedCount === 0 || running}
            onClick={() => setOpen(true)}
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Bulk download
          </Button>
        </div>
      </div>
      {running && (
        <div className="space-y-1">
          <Progress value={pct} />
          <p className="text-xs text-muted-foreground">
            {progress.label || `Processing ${progress.done} / ${progress.total}`}
          </p>
        </div>
      )}

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm bulk generation</AlertDialogTitle>
            <AlertDialogDescription>
              Generate documents for {selectedCount} student(s)?
              {isIdCard
                ? ' Merged PDF arranges cards on print sheets using your layout settings.'
                : ' This may take a few minutes for large selections.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <Label>Output format</Label>
            <Select value={output} onValueChange={setOutput}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {isIdCard ? (
                  <>
                    <SelectItem value="merged">Print sheet PDF (grid layout)</SelectItem>
                    <SelectItem value="zip">ZIP (one 2×3 PDF per student)</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="zip">ZIP (individual PDFs)</SelectItem>
                    <SelectItem value="merged">Single merged PDF</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>Generate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
